import Controller from '@ember/controller';
import { type Registry as Services, service } from '@ember/service';
import { cached } from '@glimmer/tracking';
import type { RecommendationStrategy } from 'lumen/models';
import type { RecommendationsModel } from 'lumen/routes/hotel/settings/recommendations';
import type { HotelAndMarketServiceWithHotel } from 'lumen/services/hotel-and-market';

export default class HotelSettingsRecommendationsController extends Controller {
  declare model: RecommendationsModel;

  // eslint-disable-next-line ember/no-unused-services
  @service declare access: Services['access'];
  @service('hotel-and-market')
  declare hotelAndMarketService: HotelAndMarketServiceWithHotel;
  @service declare router: Services['router'];
  @service declare store: Services['store'];

  get isIndexRoute(): boolean {
    return (
      this.router.currentRouteName === 'hotel.settings.recommendations.index'
    );
  }

  @cached
  get strategiesPerType(): {
    allowedRecommendationTypeStrategy: RecommendationStrategy;
    competitorImpactStrategies: RecommendationStrategy[];
    minimumDifferenceStrategy: RecommendationStrategy;
    priceRangeStrategies: RecommendationStrategy[];
    rollingReferenceRatesStrategy: RecommendationStrategy;
    roomOffsetHierarchyStrategy: RecommendationStrategy;
    roundingStrategy: RecommendationStrategy;
  } {
    const strategyPerType: {
      allowedRecommendationTypeStrategy: RecommendationStrategy;
      competitorImpactStrategies: RecommendationStrategy[];
      minimumDifferenceStrategy: RecommendationStrategy;
      priceRangeStrategies: RecommendationStrategy[];
      rollingReferenceRatesStrategy: RecommendationStrategy;
      roomOffsetHierarchyStrategy: RecommendationStrategy;
      roundingStrategy: RecommendationStrategy;
    } = {
      allowedRecommendationTypeStrategy: {} as RecommendationStrategy,
      minimumDifferenceStrategy: {} as RecommendationStrategy,
      roomOffsetHierarchyStrategy: {} as RecommendationStrategy,
      rollingReferenceRatesStrategy: {} as RecommendationStrategy,
      roundingStrategy: {} as RecommendationStrategy,
      priceRangeStrategies: [],
      competitorImpactStrategies: [],
    };

    // This only happens in tests so far, but because of the willDestroy handlers present in the child components
    // This getter is updated and called again after the store is destroyed, which causes an error. Hence the extra checks.
    if (this.store.isDestroyed || this.store.isDestroying || !this.model) {
      return strategyPerType;
    }

    if (!this.model.pricingAssistantRecommendationsStrategiesTask.isRunning) {
      const strategies = this.store.peekAll<RecommendationStrategy>(
        'recommendation-strategy',
      );

      strategies.forEach((strategy) => {
        // Note: Currently, we peek the store for all strategies (seasonal strategies can have user created strategies that are not persisted in BE before save). This can cause problems in case strategies are not unloaded on route change (eg. subscription change)
        // Therefore, for now we add an extra safety check to make sure we only show strategies for the current hotel
        if (
          strategy.subscriptionId.toString() !==
          this.hotelAndMarketService.hotel.id
        ) {
          return;
        }

        if (strategy.settingsType === 'allowed_recommendation_type') {
          strategyPerType.allowedRecommendationTypeStrategy = strategy;
        } else if (strategy.settingsType === 'minimum_difference') {
          strategyPerType.minimumDifferenceStrategy = strategy;
        } else if (strategy.settingsType === 'rounding') {
          strategyPerType.roundingStrategy = strategy;
        } else if (strategy.settingsType === 'price_range') {
          strategyPerType.priceRangeStrategies.push(strategy);
        } else if (strategy.settingsType === 'compset_positioning') {
          strategyPerType.competitorImpactStrategies.push(strategy);
        } else if (strategy.settingsType === 'room_offset_hierarchy') {
          strategyPerType.roomOffsetHierarchyStrategy = strategy;
        } else if (strategy.settingsType === 'rolling_reference_rates') {
          strategyPerType.rollingReferenceRatesStrategy = strategy;
        }
      });
    }

    return strategyPerType;
  }
}
